// Clinical scenarios reference content.
// row.color: "green" (1st line) | "amber" (2nd / alternative) | "blue" (adjunct / rinse / steroid / nasal) | "red" (emergency / avoid)
// note.color: same palette
// scenario.kind defaults to "rows". Other kinds: "ladder", "cards"

export const SCENARIO_TABS = [
  { id: "Infections", icon: "🦠" },
  { id: "Surgical", icon: "🪥" },
  { id: "Prophylaxis", icon: "🛡️" },
  { id: "Special Patients", icon: "👥" },
  { id: "Pain & Swelling", icon: "💢" },
  { id: "Other", icon: "📋" },
];

export const SCENARIOS = {
  "Infections": [
    {
      id: "odontogenic",
      title: "Odontogenic infection / dental abscess",
      subtitle: "Mixed aerobic/anaerobic flora",
      rows: [
        { label: "No PCN allergy · 1st", color: "green", text: "Amoxicillin 500 mg PO q8h × 5–7 days" },
        { label: "No PCN allergy · 2nd", color: "amber", text: "Amoxicillin/Clavulanate 875/125 mg PO q12h × 7 days (if no improvement 48–72h)" },
        { label: "Adjunct", color: "blue", text: "+ Metronidazole 500 mg PO q8h (severe/spreading)" },
        { label: "Non-anaph allergy · 1st", color: "green", text: "Cephalexin 500 mg PO q6h × 5–7 days" },
        { label: "Anaph allergy · 1st", color: "green", text: "Clindamycin 300 mg PO q6h × 5–7 days" },
        { label: "Anaph allergy · alt", color: "amber", text: "Azithromycin Z-pack" },
      ],
      notes: [{ color: "amber", text: "Drain the source whenever possible. Reserve clindamycin for anaphylactic PCN allergy — C. diff risk." }],
    },
    {
      id: "pericoronitis",
      title: "Pericoronitis",
      subtitle: "Partially erupted third molar",
      notes_top: [{ color: "green", text: "Mild — irrigation + chlorhexidine only. Antibiotics only if systemic signs (fever, trismus, lymphadenopathy)." }],
      rows: [
        { label: "Moderate–severe · 1st", color: "green", text: "Amoxicillin 500 mg q8h × 5 days + refer for extraction" },
        { label: "Severe", color: "amber", text: "Amoxicillin 500 mg + Metronidazole 400 mg both q8h" },
        { label: "Non-anaph allergy", color: "green", text: "Cephalexin 500 mg q6h × 5 days" },
        { label: "Anaph allergy", color: "green", text: "Clindamycin 300 mg q6h × 5 days" },
      ],
    },
    {
      id: "nug-nup",
      title: "NUG / NUP",
      subtitle: "Necrotizing periodontal disease — anaerobic-dominant",
      rows: [
        { label: "1st", color: "green", text: "Metronidazole 500 mg PO q8h × 5–7 days + debridement" },
        { label: "Severe", color: "amber", text: "Amoxicillin 500 mg + Metronidazole 500 mg both q8h × 5–7 days" },
      ],
      notes: [{ color: "blue", text: "Adjunct chlorhexidine 0.12% BID. Rule out immunosuppression." }],
    },
    {
      id: "herpes",
      title: "Herpes labialis / primary herpetic gingivostomatitis",
      subtitle: "HSV-1 — most effective within 48–72h of onset",
      rows: [
        { label: "Recurrent · 1st", color: "green", text: "Valacyclovir 2g q12h × 1 day PO at first prodrome" },
        { label: "Recurrent · alt", color: "amber", text: "Acyclovir 400 mg 5×/day × 5 days" },
        { label: "Primary · 1st", color: "green", text: "Acyclovir 400 mg 5×/day × 7–10 days PO" },
      ],
      notes: [{ color: "blue", text: "Viscous lidocaine rinse for pain. Encourage fluids — dehydration risk." }],
    },
    {
      id: "candidiasis",
      title: "Oral candidiasis",
      subtitle: "Pseudomembranous (thrush) or erythematous",
      rows: [
        { label: "Mild/localized · 1st", color: "green", text: "Nystatin suspension 400,000–600,000 units swish-and-swallow q6h × 14 days" },
        { label: "Moderate–severe · 1st", color: "green", text: "Fluconazole 200 mg day 1, then 100 mg daily × 7–14 days" },
      ],
      notes: [{ color: "amber", text: "Fluconazole interacts with warfarin (↑ INR) and Journavx (CYP3A4). Remove dentures nightly and soak in nystatin." }],
    },
  ],

  "Surgical": [
    {
      id: "sinus-lift",
      title: "Sinus lift / augmentation",
      subtitle: "Lateral window or crestal approach",
      rows: [
        { label: "PRE-OP · Abx", color: "green", text: "Amoxicillin 500 mg PO start 1 day before, or 2g single dose 1h pre-op" },
        { label: "PRE-OP · Non-anaph allergy", color: "green", text: "Cephalexin 500 mg PO start 1 day before" },
        { label: "PRE-OP · Anaph allergy", color: "green", text: "Clindamycin 300 mg PO start 1 day before" },
        { label: "PRE-OP · Steroid", color: "blue", text: "Dexamethasone 8 mg PO morning of surgery" },
        { label: "PRE-OP · Nasal", color: "blue", text: "Oxymetazoline (Afrin) 2 sprays each nostril 30 min before" },
        { label: "POST-OP · Abx", color: "green", text: "Amoxicillin 500 mg q8h × 7–10 days" },
        { label: "POST-OP · Nasal", color: "blue", text: "Saline nasal rinse (NeilMed) BID start day after × 2 weeks" },
        { label: "POST-OP · Nasal", color: "blue", text: "Fluticasone (Flonase) 1–2 sprays each nostril daily × 2 weeks" },
        { label: "POST-OP · Pain", color: "amber", text: "Ibuprofen 600 mg + Acetaminophen 500 mg alternating q4h" },
      ],
      notes: [{ color: "amber", text: "No nose blowing, no sneezing with closed mouth, no straws, no smoking × 2 weeks. If membrane perforation: extend abx to 14 days." }],
    },
    {
      id: "implant-placement",
      title: "Implant placement",
      subtitle: "Single tooth or multiple — evidence-based protocol",
      notes_top: [{ color: "blue", text: "Evidence is mixed on routine antibiotics. Higher-risk patients (smokers, diabetics, grafted sites) benefit most." }],
      rows: [
        { label: "Standard · Pre-op", color: "green", text: "Amoxicillin 2g PO 1h before" },
        { label: "Standard · Post-op", color: "green", text: "Amoxicillin 500 mg q8h × 5–7 days (high-risk/graft)" },
        { label: "Non-anaph · Pre-op", color: "green", text: "Cephalexin 2g PO 1h before" },
        { label: "Non-anaph · Post-op", color: "green", text: "Cephalexin 500 mg q6h × 5–7 days" },
        { label: "Anaph · Pre-op", color: "green", text: "Clindamycin 600 mg PO 1h before" },
        { label: "Anaph · Post-op", color: "green", text: "Clindamycin 300 mg q6h × 5–7 days" },
        { label: "Rinse", color: "blue", text: "Chlorhexidine 0.12% BID start 1 day pre-op, continue × 2 weeks" },
      ],
    },
    {
      id: "third-molar",
      title: "Third molar / surgical extraction",
      subtitle: "Post-op protocol — antibiotics not routine",
      notes_top: [{ color: "green", text: "Antibiotics NOT routinely indicated for uncomplicated extractions in healthy patients." }],
      rows: [
        { label: "Pain (all)", color: "amber", text: "Ibuprofen 600 mg + Acetaminophen 500 mg alternating q4–6h × 3–5 days" },
        { label: "Steroid", color: "blue", text: "Dexamethasone 8 mg PO/IM pre-op" },
        { label: "High-risk · Abx", color: "green", text: "Amoxicillin 500 mg q8h × 5 days (diabetic, immunocompromised)" },
        { label: "Non-anaph allergy", color: "green", text: "Cephalexin 500 mg q6h × 5 days" },
        { label: "Anaph allergy", color: "green", text: "Clindamycin 300 mg q6h × 5 days" },
      ],
      notes: [{ color: "amber", text: "Dry socket: Antibiotics NOT indicated. Local irrigation + dressing (Alvogyl). NSAIDs for pain." }],
    },
    {
      id: "ridge-aug",
      title: "Ridge augmentation / bone grafting",
      subtitle: "GBR, socket preservation, block graft",
      rows: [
        { label: "Pre-op", color: "green", text: "Amoxicillin 2g PO 1h before" },
        { label: "Post-op", color: "green", text: "Amoxicillin 500 mg q8h × 7–10 days" },
        { label: "Non-anaph allergy", color: "green", text: "Cephalexin 500 mg q6h × 7–10 days" },
        { label: "Anaph allergy", color: "green", text: "Clindamycin 300 mg q6h × 7–10 days" },
        { label: "Rinse", color: "blue", text: "Chlorhexidine 0.12% BID × 2 weeks" },
      ],
    },
  ],

  "Prophylaxis": [
    {
      id: "ie-indications",
      title: "IE prophylaxis — indications (AHA 2021)",
      subtitle: "High-risk cardiac conditions only",
      kind: "cards",
      cards_title: "IS indicated",
      cards: [
        { title: "Prosthetic heart valve", body: "Including transcatheter / homografts" },
        { title: "Previous IE", body: "Any history of infective endocarditis" },
        { title: "Congenital heart disease", body: "Unrepaired cyanotic; repaired with residual defects; first 6 months after repair" },
        { title: "Cardiac transplant with valvulopathy", body: "" },
      ],
      notes: [
        { color: "blue", text: "NOT indicated: MVP, bicuspid aortic valve, rheumatic heart disease, pacemakers, ICDs, stents, CABG no longer qualify." },
        { color: "green", text: "Qualifying procedures: manipulation of gingival tissue, periapical region, or oral mucosa perforation. Routine injections, X-rays, and ortho bracket placement do NOT qualify." },
      ],
    },
    {
      id: "ie-regimens",
      title: "IE prophylaxis regimens",
      subtitle: "Single dose 30–60 min before procedure",
      rows: [
        { label: "No allergy · oral", color: "green", text: "Amoxicillin 2g PO" },
        { label: "No allergy · NPO", color: "green", text: "Ampicillin 2g IM/IV" },
        { label: "Non-anaph PCN · oral", color: "green", text: "Cephalexin (Keflex) 2g PO (safe, ~2% cross-reactivity)" },
        { label: "Anaph PCN · oral · 1st", color: "green", text: "Clindamycin 600 mg PO" },
        { label: "Anaph PCN · oral · alt", color: "amber", text: "Azithromycin 500 mg PO" },
        { label: "Anaph PCN · oral · alt", color: "amber", text: "Clarithromycin 500 mg PO" },
        { label: "Anaph PCN · NPO", color: "green", text: "Clindamycin 600 mg IM/IV" },
      ],
      notes: [{ color: "amber", text: "Never use cephalosporins in anaphylactic/urticarial PCN allergy." }],
    },
    {
      id: "joint-prophylaxis",
      title: "Joint replacement prophylaxis (ADA/AAOS 2014)",
      subtitle: "Not routinely recommended",
      notes_top: [{ color: "green", text: "Routine prophylaxis NOT recommended for most patients with prosthetic joints." }],
      kind: "cards",
      cards_title: "Consider for",
      cards: [
        { title: "Immunocompromised", body: "" },
        { title: "First 2 years post-implant", body: "If orthopedist requests" },
        { title: "Previous prosthetic joint infection", body: "" },
        { title: "Poorly controlled diabetes", body: "" },
      ],
      notes: [{ color: "amber", text: "Always defer to orthopedic surgeon's written request. If requested: amoxicillin 2g PO, or cephalexin 2g (non-anaph allergy), or clindamycin 600 mg (anaph allergy)." }],
    },
  ],

  "Special Patients": [
    {
      id: "anticoagulated",
      title: "Anticoagulated patient (warfarin / DOACs)",
      subtitle: "Continue anticoagulation for most routine procedures",
      notes_top: [{ color: "green", text: "Current evidence — continue anticoagulation. Risk of thromboembolic event from interruption outweighs bleeding risk." }],
      rows: [
        { label: "Warfarin · INR ≤3.5", color: "green", text: "Proceed without stopping; local hemostasis (sutures, Surgicel, tranexamic acid rinse)" },
        { label: "Warfarin · INR >3.5", color: "amber", text: "Consult prescribing physician; do not proceed electively" },
        { label: "DOAC · routine", color: "green", text: "Continue DOAC, schedule at trough (12h after BID dose, 24h after QD)" },
        { label: "DOAC · major surgery", color: "amber", text: "Consult physician" },
      ],
      notes: [{ color: "amber", text: "Avoid NSAIDs — use acetaminophen. Avoid metronidazole and augmentin (potentiate warfarin)." }],
    },
    {
      id: "diabetic",
      title: "Diabetic patient",
      subtitle: "Surgical and infection management",
      notes_top: [{ color: "blue", text: "Well-controlled HbA1c <7% — minimal risk. Poorly controlled HbA1c >9% — use antibiotics more liberally." }],
      rows: [
        { label: "Infection/surgical · 1st", color: "green", text: "Amoxicillin/Clavulanate 875/125 mg q12h × 7 days" },
        { label: "Hypoglycemia (conscious)", color: "blue", text: "15–20g oral glucose (OJ, tablets, candy) — recheck in 15 min" },
        { label: "Hypoglycemia (unconscious)", color: "red", text: "Glucagon 1 mg IM + call 911" },
      ],
      notes: [{ color: "amber", text: "Schedule morning appointments. Dexamethasone raises blood glucose — use cautiously." }],
    },
    {
      id: "pregnant",
      title: "Pregnant patient",
      subtitle: "Drug safety by trimester",
      rows: [
        { label: "Safe abx", color: "green", text: "Amoxicillin, Amoxicillin/Clavulanate, Cephalexin (preferred throughout pregnancy)" },
        { label: "Safe abx", color: "green", text: "Clindamycin (safe if needed for anaph PCN allergy)" },
        { label: "Avoid abx", color: "red", text: "Metronidazole (avoid 1st trimester; caution 2nd/3rd)" },
        { label: "Avoid abx", color: "red", text: "Tetracyclines/doxycycline (contraindicated — tooth discoloration)" },
        { label: "Analgesia · safe", color: "green", text: "Acetaminophen (first-line all trimesters)" },
        { label: "Analgesia · avoid", color: "red", text: "NSAIDs after 20 weeks (ductus arteriosus closure risk)" },
        { label: "Analgesia · caution", color: "amber", text: "Opioids (short course only if essential)" },
      ],
      notes: [{ color: "green", text: "Elective procedures best in 2nd trimester (weeks 14–20). Local anesthetics with epinephrine safe at standard dental doses." }],
    },
    {
      id: "bisphosphonate",
      title: "Bisphosphonate / antiresorptive therapy (MRONJ risk)",
      subtitle: "IV vs. oral — risk stratification",
      rows: [
        { label: "Oral <4 years", color: "green", text: "Proceed — low risk. No drug holiday needed. Document and inform patient." },
        { label: "Oral >4 years / + steroids", color: "amber", text: "Consider 2-month drug holiday before and after surgery if medically permissible." },
        { label: "IV bisphosphonates", color: "red", text: "High MRONJ risk — avoid elective surgery. Consult oncologist. Primary closure critical." },
        { label: "If extraction unavoidable", color: "amber", text: "Amoxicillin 500 mg start 1 day before, q8h × 14 days post-op" },
        { label: "If extraction unavoidable", color: "blue", text: "Chlorhexidine 0.12% BID until healed" },
      ],
    },
    {
      id: "pediatric",
      title: "Pediatric patient",
      subtitle: "Weight-based dosing",
      rows: [
        { label: "Abx · 1st", color: "green", text: "Amoxicillin 25–45 mg/kg/day PO divided q8h (max 500 mg/dose)" },
        { label: "Abx · non-anaph", color: "green", text: "Cephalexin 25–50 mg/kg/day divided q6–8h" },
        { label: "Abx · anaph", color: "green", text: "Clindamycin 10–25 mg/kg/day divided q6–8h (max 300 mg/dose)" },
        { label: "Analgesia · 1st", color: "green", text: "Ibuprofen 5–10 mg/kg PO q6–8h (max 400 mg/dose)" },
        { label: "Analgesia · alt", color: "amber", text: "Acetaminophen 10–15 mg/kg q4–6h (max 75 mg/kg/day)" },
        { label: "IE prophy", color: "green", text: "Amoxicillin 50 mg/kg single dose (max 2g)" },
        { label: "IE · non-anaph", color: "green", text: "Cephalexin 50 mg/kg (max 2g)" },
        { label: "IE · anaph", color: "green", text: "Clindamycin 20 mg/kg (max 600 mg)" },
      ],
      notes: [{ color: "amber", text: "Never use aspirin in children — Reye's syndrome." }],
    },
  ],

  "Pain & Swelling": [
    {
      id: "analgesic-ladder",
      title: "Acute dental pain — analgesic ladder",
      subtitle: "Step up based on severity",
      kind: "ladder",
      steps: [
        { level: "Mild", text: "Ibuprofen 400–600 mg PO q6–8h with food. Max 3.2g/day." },
        { level: "Moderate", text: "Ibuprofen 600 mg + Acetaminophen 500–1000 mg PO together q6–8h. Synergistic — superior to either alone." },
        { level: "Moderate–severe", text: "Suzetrigine (Journavx) 100 mg loading × 1, then 50 mg q12h PO on empty stomach. Non-opioid Nav1.8 blocker. No abuse potential, no sedation.", badge: "NEW 2025" },
        { level: "Severe post-surgical", text: "Oxycodone 5 mg or Hydrocodone 5 mg PO q4–6h PRN × 2–3 days max. Check PDMP. Minimum effective dose." },
      ],
    },
    {
      id: "journavx",
      title: "Journavx (suzetrigine) — full monograph",
      subtitle: "Selective Nav1.8 blocker — non-opioid, non-NSAID",
      badge: "FDA Jan 2025",
      rows: [
        { label: "Dose", color: "green", text: "100 mg loading × 1, then 50 mg q12h PO on empty stomach (food reduces absorption ~45%)", highlight: true },
        { label: "Ideal · SUD history", color: "blue", text: "No abuse potential", highlight: true },
        { label: "Ideal · CNS depressants", color: "blue", text: "No additive respiratory depression with concurrent benzos or sedatives", highlight: true },
        { label: "Ideal · elderly/fall-risk", color: "blue", text: "No sedation, confusion, or constipation", highlight: true },
        { label: "Ideal · NSAID contraind.", color: "blue", text: "GI ulcer, renal impairment, anticoagulated", highlight: true },
        { label: "Avoid", color: "red", text: "Fluconazole (strong CYP3A4 inhibitor — common in dentistry for candidiasis)" },
      ],
      notes: [{ color: "amber", text: "Also avoid with clarithromycin, ketoconazole. Cost ~$15/tablet (2025) — check formulary." }],
    },
    {
      id: "swelling-trismus",
      title: "Post-op swelling and trismus",
      subtitle: "After extractions, implants, grafts",
      rows: [
        { label: "1st", color: "green", text: "Dexamethasone 8 mg PO/IM single perioperative dose (most effective for swelling/trismus)" },
        { label: "Extended", color: "amber", text: "Methylprednisolone dose pack PO 6-day taper (bilateral/major surgery)" },
        { label: "NSAIDs", color: "blue", text: "Ibuprofen 600 mg q6h around-the-clock × 3–4 days post-op" },
      ],
      notes: [{ color: "amber", text: "Corticosteroids raise blood glucose — caution in diabetics." }],
    },
    {
      id: "tmj",
      title: "TMJ / orofacial pain",
      subtitle: "Acute flare, myofascial pain",
      rows: [
        { label: "NSAID", color: "green", text: "Naproxen 500 mg BID × 7–14 days (longer duration preferred for TMJ)" },
        { label: "Muscle", color: "amber", text: "Cyclobenzaprine 5–10 mg QHS or TID × 1–2 weeks (short-term only)" },
        { label: "Steroid", color: "blue", text: "Methylprednisolone dose pack PO 6-day taper (acute severe flare)" },
      ],
      notes: [{ color: "blue", text: "Refer to physical therapy + occlusal splint concurrently. Avoid long-term opioid/benzo use for TMD." }],
    },
  ],

  "Other": [
    {
      id: "emergencies",
      title: "Dental office emergencies",
      subtitle: "Anaphylaxis, angina, hypoglycemia",
      rows: [
        { label: "Anaphylaxis · 1st ALWAYS", color: "red", text: "Epinephrine 0.3 mg IM lateral thigh (EpiPen), repeat q5–15 min, call 911" },
        { label: "Anaphylaxis · adjunct", color: "blue", text: "Diphenhydramine 50 mg IM after epinephrine only" },
        { label: "Acute angina · 1st", color: "red", text: "Nitroglycerin 0.4 mg sublingual, repeat q5 min × 3 max, call 911 if no relief" },
        { label: "Hypoglycemia · conscious", color: "blue", text: "15–20g oral glucose, recheck in 15 min" },
        { label: "Hypoglycemia · unconscious", color: "red", text: "Glucagon 1 mg IM + call 911" },
      ],
      notes: [{ color: "amber", text: "Ask about PDE-5 inhibitors before giving nitro — fatal hypotension." }],
    },
    {
      id: "aphthous",
      title: "Aphthous ulcers (canker sores)",
      subtitle: "Minor, major, or herpetiform",
      rows: [
        { label: "Topical", color: "green", text: "Triamcinolone 0.1% paste (Kenalog in Orabase) apply 2–4×/day until healed" },
        { label: "Rinse", color: "blue", text: "Magic mouthwash (lidocaine/diphenhydramine/antacid equal parts) swish 1 tsp q4–6h PRN" },
        { label: "Severe/major", color: "amber", text: "Prednisone 40–60 mg PO taper over 1–2 weeks" },
      ],
      notes: [{ color: "blue", text: "Rule out B12/folate/iron deficiency, celiac disease, or IBD in recurrent cases." }],
    },
    {
      id: "xerostomia",
      title: "Xerostomia (dry mouth)",
      subtitle: "Often medication-induced — 500+ drugs implicated",
      rows: [
        { label: "OTC", color: "blue", text: "Biotène spray, gel, or rinse PRN" },
        { label: "Fluoride", color: "green", text: "Fluoride varnish 5% NaF q3–6 months (mandatory in xerostomia)" },
        { label: "Rx", color: "amber", text: "Pilocarpine 5 mg TID PO (sialagogue — requires medical clearance)" },
      ],
      notes: [{ color: "amber", text: "Identify causative medications (antihistamines, antidepressants, antihypertensives) and discuss with prescribing physician." }],
    },
    {
      id: "anxiety",
      title: "Dental anxiety / oral sedation",
      subtitle: "Pre-medication options for anxious patients",
      rows: [
        { label: "1st", color: "green", text: "Triazolam 0.125–0.25 mg PO 30–60 min before (preferred — short half-life)" },
        { label: "Alt", color: "amber", text: "Diazepam 5–10 mg PO 1h before (longer duration)" },
        { label: "Inhalation", color: "blue", text: "Nitrous oxide 30–50% titrated (patient can drive after O₂ recovery)" },
      ],
      notes: [{ color: "amber", text: "All oral sedation patients require a driver. Contraindicated with CNS depressants or respiratory disease." }],
    },
  ],
};

export const CATEGORIES = [
  "Analgesic", "Analgesic/NSAID", "Antibiotic", "Antifungal", "Antiviral",
  "Antihistamine", "Anxiolytic", "Corticosteroid", "Emergency", "Local Anesthetic",
  "Opioid Analgesic", "Antiseptic Rinse", "Topical Preventive",
];

export const CATEGORY_COLORS = {
  "Analgesic": "bg-blue-50 text-blue-700 border-blue-200",
  "Analgesic/NSAID": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Antibiotic": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Antifungal": "bg-purple-50 text-purple-700 border-purple-200",
  "Antiviral": "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Antihistamine": "bg-pink-50 text-pink-700 border-pink-200",
  "Anxiolytic": "bg-violet-50 text-violet-700 border-violet-200",
  "Corticosteroid": "bg-orange-50 text-orange-700 border-orange-200",
  "Emergency": "bg-red-50 text-red-700 border-red-200",
  "Local Anesthetic": "bg-teal-50 text-teal-700 border-teal-200",
  "Opioid Analgesic": "bg-rose-50 text-rose-700 border-rose-200",
  "Antiseptic Rinse": "bg-sky-50 text-sky-700 border-sky-200",
  "Topical Preventive": "bg-amber-50 text-amber-700 border-amber-200",
};

export const ROW_COLORS = {
  green: { bg: "bg-emerald-50", border: "border-emerald-200", label: "bg-[#1D9E75] text-white", text: "text-emerald-900" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", label: "bg-amber-500 text-white", text: "text-amber-900" },
  blue: { bg: "bg-sky-50", border: "border-sky-200", label: "bg-sky-600 text-white", text: "text-sky-900" },
  red: { bg: "bg-rose-50", border: "border-rose-200", label: "bg-rose-600 text-white", text: "text-rose-900" },
};

export const NOTE_COLORS = {
  green: "bg-emerald-50 border-emerald-200 text-emerald-900",
  blue: "bg-sky-50 border-sky-200 text-sky-900",
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  red: "bg-rose-50 border-rose-200 text-rose-900",
};
